import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HealthBadge } from "@/components/HealthBadge";
import { BannerFuentes } from "@/components/BannerFuentes";
import { fetchCandidatos, fmtVotos, fmtPct, fmtFecha } from "@/lib/candidatos";

const HALLAZGOS_BASE = [
  {
    id: "H1",
    cat: "probado",
    emoji: "🟢",
    titulo: "34.2% de mesas de Lima operó fuera del horario legal",
    cifra: "9,319 / 27,247 mesas",
    resumen:
      "Instaladas después de las 08:00h. 1,454 superaron 10:00h → causal Art. 363 LOE. 1,516 mesas sin acta de instalación.",
    href: "/manipulacion-electoral",
  },
  {
    id: "H2",
    cat: "probado",
    emoji: "🟢",
    titulo: "La tardanza cambió la distribución de votos, no solo el ausentismo",
    cifra: "Δ RLA +7.85pp en mesas tardías",
    resumen:
      "Mesas a tiempo (07h): RLA 29.47% · KFH 27.49%. Mesas tardías (≥08h): RLA 36.73% · KFH 24.53%. Sistemático en 7,803 mesas.",
    href: "/manipulacion-electoral",
  },
  {
    id: "H4",
    cat: "hipotesis",
    emoji: "🟡",
    titulo: "Las 4,703 mesas 900K pueden carecer de base legal para EG2026",
    cifra: "Sánchez 41.84% vs ~12% nacional",
    resumen:
      "3 mesas 900K sin CCPP asignado. Ley 28.440 aplica solo a elecciones de municipalidades de CCPP, no a EG. Δ +29.81pp.",
    href: "/mesas-900k",
  },
  {
    id: "H5",
    cat: "probado",
    emoji: "🟢",
    titulo: "Ausentismo 2026 = segundo más alto en 30 años sin causa exógena",
    cifra: "+1,455,272 electores no votaron vs 2016",
    resumen:
      "24.20% nacional vs 18.20% en 2016 (línea base sin pandemia). Lima Metro: 479,631 votos excedentes perdidos.",
    href: "/manipulacion-electoral",
  },
];

const SECCIONES = [
  { to: "/candidatos", titulo: "Candidatos", sub: "Auditoría de los 36 + blanco + nulo" },
  { to: "/expediente-electoral", titulo: "Expediente electoral", sub: "Repositorio documental central" },
  { to: "/trazabilidad", titulo: "Trazabilidad", sub: "Cadena mesa → acta → JEE → resultado" },
  { to: "/reniec", titulo: "RENIEC", sub: "Padrón EG2026 y normativa" },
  { to: "/onpe", titulo: "ONPE", sub: "Regulación, STAE y Gálaga" },
  { to: "/jne", titulo: "JNE", sub: "Acuerdo 23-abr y resoluciones" },
  { to: "/caso-galaga", titulo: "Caso Gálaga", sub: "Expediente del contrato logístico" },
  { to: "/mesas-900k", titulo: "Mesas 900K", sub: "Investigación CCPP y base legal" },
  { to: "/manipulacion-electoral", titulo: "Manipulación electoral", sub: "Mesas tardías · análisis técnico" },
  { to: "/laboratorio", titulo: "Laboratorio", sub: "Datasets reproducibles" },
];

const chipClass = (cat: string) =>
  ({
    probado: "chip-probado",
    reconocido: "chip-reconocido",
    hipotesis: "chip-hipotesis",
    alegacion: "chip-alegacion",
    vacio: "chip-vacio",
  })[cat] ?? "chip-vacio";

export function HomePage() {
  // Hallazgo H3 dinámico desde data oficial (no hardcoded)
  const { data } = useQuery({ queryKey: ["candidatos-home"], queryFn: () => fetchCandidatos() });

  const segundo = data?.candidatos.find((c) => c.tipo === "candidato" && c.ranking === 3); // rank 2 es voto blanco
  const tercero = data?.candidatos.find((c) => c.tipo === "candidato" && c.ranking === 4);
  const deltaSegundoTercero =
    segundo && tercero ? segundo.votosAbsolutos - tercero.votosAbsolutos : null;
  const snapshotFecha = data?.proceso ? fmtFecha(data.proceso.fetchedAt) : "—";

  const hallazgoH3 = {
    id: "H3",
    cat: "reconocido",
    emoji: "🔵",
    titulo: "1,071,111 electores en 256 locales con retraso de material",
    cifra:
      deltaSegundoTercero !== null
        ? `Δ 2°-3° = ${fmtVotos(deltaSegundoTercero)} votos · 55,261 sin votar 12/4`
        : "55,261 sin votar el 12/4 · Δ 2°-3° (cargando…)",
    resumen:
      segundo && tercero
        ? `13 locales sin material definitivo. Diferencia entre ${segundo.iniciales ?? segundo.nombreCandidato} (${fmtVotos(segundo.votosAbsolutos)}) y ${tercero.iniciales ?? tercero.nombreCandidato} (${fmtVotos(tercero.votosAbsolutos)}) = ${fmtVotos(deltaSegundoTercero!)} votos. Factor de impacto: 55,261 / ${fmtVotos(deltaSegundoTercero!)} ≈ ${(55261 / deltaSegundoTercero!).toFixed(2)}x. Snapshot ONPE ${snapshotFecha}.`
        : "13 locales sin material definitivo. Acuerdo del Pleno JNE 23/04/2026.",
    href: "/jne",
  };

  const hallazgos = [HALLAZGOS_BASE[0]!, HALLAZGOS_BASE[1]!, hallazgoH3, HALLAZGOS_BASE[2]!, HALLAZGOS_BASE[3]!];

  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-3">
        <span className="chip-reconocido">EG2026 · Presidencial</span>
        <HealthBadge />
        {data?.proceso && (
          <span className="chip-probado" title={data.proceso.fuente}>
            🟢 Datos ONPE oficiales · {snapshotFecha}
          </span>
        )}
      </div>
      <h1 className="mt-6 font-serif text-4xl leading-tight text-ink-50 md:text-5xl">
        La verdad del proceso electoral EG2026,{" "}
        <span className="text-gold-300">documentada</span>.
      </h1>
      <p className="mt-4 max-w-3xl text-lg text-ink-200">
        Centro ciudadano de documentación, trazabilidad y análisis técnico-electoral.
        Conectamos evidencia, expedientes oficiales, peritajes y testimonios en una arquitectura
        verificable y auditable. <em>Distorsión electoral, no fraude</em>: solo afirmamos lo que la
        evidencia sostiene.
      </p>

      <BannerFuentes pagina="/" />

      {/* Resumen oficial vivo */}
      {data?.proceso && (
        <section className="mt-8 grid gap-3 md:grid-cols-4">
          <KpiCard
            label="Electores hábiles"
            value={data.proceso.electoresHabiles ? fmtVotos(data.proceso.electoresHabiles) : "—"}
          />
          <KpiCard
            label="Participación"
            value={fmtPct(data.proceso.participacion, 2)}
            hint={`${fmtVotos(data.proceso.totalEmitidos)} votos emitidos`}
          />
          <KpiCard
            label="Votos válidos"
            value={fmtVotos(data.proceso.totalValidos)}
            hint={`Blancos ${fmtVotos(data.proceso.votosBlancos)} · Nulos ${fmtVotos(data.proceso.votosNulos)}`}
          />
          <KpiCard
            label="Actas contabilizadas"
            value={fmtPct(data.proceso.pctContabilizadas, 2)}
            hint={`${fmtVotos(data.proceso.contabilizadas)} / ${fmtVotos(data.proceso.totalActas)} actas`}
          />
        </section>
      )}

      <section className="mt-12">
        <h2 className="text-xl font-semibold text-ink-50">Los 5 hallazgos verificados</h2>
        <p className="mt-1 text-sm text-ink-300">
          Cada hallazgo tiene su categoría epistemológica y fuente primaria.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {hallazgos.map((h) => (
            <Link key={h.id} to={h.href} className="card-ink block">
              <div className="flex items-center justify-between gap-3">
                <span className={chipClass(h.cat)}>
                  {h.emoji} {h.cat.replace("_", " ")}
                </span>
                <span className="text-xs text-ink-400">{h.id}</span>
              </div>
              <h3 className="mt-3 font-serif text-lg text-ink-50">{h.titulo}</h3>
              <div className="mt-2 text-gold-300 text-sm font-medium">{h.cifra}</div>
              <p className="mt-2 text-sm leading-relaxed text-ink-200">{h.resumen}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-16">
        <h2 className="text-xl font-semibold text-ink-50">Secciones del sitio</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {SECCIONES.map((s) => (
            <Link key={s.to} to={s.to} className="card-ink group">
              <div className="text-sm uppercase tracking-widest text-gold-400/80">{s.sub}</div>
              <div className="mt-1 font-serif text-xl text-ink-50 group-hover:text-gold-200">
                {s.titulo} →
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-ink-700/60 bg-ink-900/60 p-4">
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div className="mt-1 font-mono text-2xl tabular-nums text-ink-50">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}
